export const RFQ_STAGES = Object.freeze({
  IDLE: "idle",
  QUALIFICATION: "qualification",
  COMMERCIAL_NOTES: "commercial-notes",
  REVIEW: "review",
  CONTACT: "contact",
  SUBMITTING: "submitting",
  HUMAN_HANDOVER: "human-handover",
});

export const COMMERCIAL_NOTE_MAX_LENGTH = 1000;
export const DEDICATED_COMMERCIAL_NOTE_ID = "dedicated-commercial-note";

export const EMPTY_CONTACT = Object.freeze({
  full_name: "",
  company_name: "",
  telephone: "",
  email: "",
  preferred_method: "Email",
});

export const UNCERTAINTY_OPTIONS = Object.freeze([
  "Please advise",
  "Not sure",
  "None",
  "No special requirement",
]);

export function toggleExclusiveMulti(selected, option) {
  const current = Array.isArray(selected) ? selected : [];
  if (current.includes(option)) return current.filter((item) => item !== option);
  if (UNCERTAINTY_OPTIONS.includes(option)) return [option];
  return [...current.filter((item) => !UNCERTAINTY_OPTIONS.includes(item)), option];
}

export function createInitialRfqState(service = "") {
  return {
    service,
    answers: {},
    step: 0,
    stage: service ? RFQ_STAGES.QUALIFICATION : RFQ_STAGES.IDLE,
    value: "",
    contact: { ...EMPTY_CONTACT },
    consent: false,
    documentIds: [],
    commercialComments: [],
    notice: "",
    error: "",
  };
}

const STAGE_ACTIONS = { SHOW_REVIEW: RFQ_STAGES.REVIEW, SHOW_CONTACT: RFQ_STAGES.CONTACT, SHOW_HANDOVER: RFQ_STAGES.HUMAN_HANDOVER, SUBMIT: RFQ_STAGES.SUBMITTING };
const RESET_ACTIONS = new Set(["RESET", "SELECT_SERVICE"]);
const COMMERCIAL_ACTIONS = new Set([
  "ADD_COMMERCIAL_COMMENT",
  "REMOVE_COMMERCIAL_COMMENT",
  "SHOW_COMMERCIAL_NOTES",
  "SAVE_COMMERCIAL_NOTE",
  "EDIT_COMMERCIAL_COMMENT",
]);
const restoredActionValue = (action) => action.value === undefined ? "" : action.value;

function reduceCommercialAction(state, action) {
  switch (action.type) {
    case "ADD_COMMERCIAL_COMMENT":
      return {
        ...state,
        commercialComments: [...state.commercialComments, action.note].slice(-5),
        value: "",
        notice: action.notice,
        error: "",
      };
    case "REMOVE_COMMERCIAL_COMMENT":
      return {
        ...state,
        commercialComments: state.commercialComments.filter((note) => note.id !== action.id),
        notice: "Commercial note removed. It will not be sent.",
        error: "",
      };
    case "SHOW_COMMERCIAL_NOTES": {
      const existing = state.commercialComments.find((note) => note.id === DEDICATED_COMMERCIAL_NOTE_ID);
      return {
        ...state,
        value: existing?.value || "",
        stage: RFQ_STAGES.COMMERCIAL_NOTES,
        notice: "",
        error: "",
      };
    }
    case "SAVE_COMMERCIAL_NOTE": {
      const remaining = state.commercialComments.filter((note) => note.id !== DEDICATED_COMMERCIAL_NOTE_ID);
      const commercialComments = action.value
        ? [...remaining, {
          id: DEDICATED_COMMERCIAL_NOTE_ID,
          questionId: DEDICATED_COMMERCIAL_NOTE_ID,
          questionLabel: "Commercial notes",
          source: "dedicated",
          value: action.value,
        }].slice(-5)
        : remaining;
      return {
        ...state,
        commercialComments,
        value: "",
        stage: RFQ_STAGES.REVIEW,
        notice: "",
        error: "",
      };
    }
    case "EDIT_COMMERCIAL_COMMENT":
      if (action.note.id === DEDICATED_COMMERCIAL_NOTE_ID || action.note.source === "dedicated") {
        return {
          ...state,
          value: action.note.value,
          stage: RFQ_STAGES.COMMERCIAL_NOTES,
          notice: "",
          error: "",
        };
      }
      return {
        ...state,
        commercialComments: state.commercialComments.filter((note) => note.id !== action.note.id),
        step: action.note.step,
        value: action.note.value,
        stage: RFQ_STAGES.QUALIFICATION,
        notice: "Edit the commercial note, then continue. It will return to human review if it still contains commercial intent.",
        error: "",
      };
    default:
      return state;
  }
}

export function rfqReducer(state, action) {
  if (STAGE_ACTIONS[action.type]) return { ...state, stage: STAGE_ACTIONS[action.type], error: "" };
  if (RESET_ACTIONS.has(action.type)) return createInitialRfqState(action.service || "");
  if (COMMERCIAL_ACTIONS.has(action.type)) return reduceCommercialAction(state, action);
  switch (action.type) {
    case "SET_VALUE":
      return { ...state, value: action.value, error: "" };
    case "TOGGLE_MULTI": {
      const value = toggleExclusiveMulti(state.value, action.value);
      return { ...state, value, error: "" };
    }
    case "SET_ERROR":
      return { ...state, error: action.error, stage: state.stage === RFQ_STAGES.SUBMITTING ? RFQ_STAGES.CONTACT : state.stage };
    case "CLEAR_ERROR":
      return { ...state, error: "" };
    case "ANSWER": {
      const answers = { ...state.answers, [action.questionId]: action.value };
      const dedicatedNote = state.commercialComments.find((note) => note.id === DEDICATED_COMMERCIAL_NOTE_ID);
      return action.isLast
        ? { ...state, answers, value: dedicatedNote?.value || "", stage: RFQ_STAGES.COMMERCIAL_NOTES, notice: "", error: "" }
        : { ...state, answers, value: "", step: state.step + 1, notice: "", error: "" };
    }
    case "BACK_FROM_COMMERCIAL_NOTES": {
      const step = Math.max(0, action.step);
      return {
        ...state,
        step,
        value: restoredActionValue(action),
        stage: RFQ_STAGES.QUALIFICATION,
        notice: "",
        error: "",
      };
    }
    case "BACK":
      return {
        ...state,
        step: Math.max(0, state.step - 1),
        value: restoredActionValue(action),
        error: "",
      };
    case "EDIT":
      return {
        ...state,
        step: action.step,
        value: restoredActionValue(action),
        stage: RFQ_STAGES.QUALIFICATION,
        error: "",
      };
    case "UPDATE_CONTACT":
      return { ...state, contact: { ...state.contact, [action.field]: action.value }, error: "" };
    case "SET_CONSENT":
      return { ...state, consent: action.value, error: "" };
    case "DOCUMENT_UPLOADED":
      return { ...state, documentIds: [...state.documentIds, action.documentId], error: "" };
    default:
      return state;
  }
}

export function calculateProgress(state, questionCount) {
  if (!state.service || questionCount === 0) return 0;
  if ([RFQ_STAGES.COMMERCIAL_NOTES, RFQ_STAGES.REVIEW, RFQ_STAGES.CONTACT, RFQ_STAGES.SUBMITTING, RFQ_STAGES.HUMAN_HANDOVER].includes(state.stage)) return 100;
  return Math.round(((Math.min(state.step, questionCount - 1) + 1) / questionCount) * 100);
}

export function normaliseCommercialNote(value) {
  if (typeof value !== "string") throw new TypeError("Commercial note must be text.");
  const note = value.trim();
  if (note.length > COMMERCIAL_NOTE_MAX_LENGTH) {
    throw new RangeError(`Commercial note must be ${COMMERCIAL_NOTE_MAX_LENGTH} characters or fewer.`);
  }
  return note;
}

export function normaliseAnswer(answer, optional = false) {
  if (Array.isArray(answer)) return answer.filter(Boolean);
  const value = String(answer ?? "").trim();
  return value || (optional ? "Not specified" : "");
}

export function buildTranscript(answers, questions) {
  const byId = new Map(questions.map((question) => [question.id, question.label]));
  return Object.entries(answers).map(([key, answer]) => ({
    role: "visitor",
    question: byId.get(key) || key,
    answer,
  }));
}

export function buildAttribution(location) {
  const params = new URLSearchParams(location.search);
  return {
    landing_page: window.sessionStorage.getItem("ac_landing") || location.pathname,
    source_page: location.pathname,
    originating_article: location.pathname.startsWith("/insights/") ? location.pathname : null,
    referrer: document.referrer || null,
    device_type: window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
    google_campaign: params.get("gclid"),
  };
}

export function buildWhatsAppMessage(reference, contact, serviceLabel, answers) {
  const shortSummary = Object.values(answers).filter(Boolean).slice(0, 3).join("; ");
  return [
    `Enquiry reference: ${reference || "Pending"}`,
    `Customer: ${contact.full_name || "Not provided"}`,
    `Company: ${contact.company_name || "Not provided"}`,
    `Service: ${serviceLabel || "Service requirement"}`,
    `Summary: ${shortSummary || "Operational details recorded"}`,
  ].join("\n");
}
